import { FC, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Routes from '@/router/Routes.ts';
import PageHeader from '@/components/ui/PageHeader/PageHeader.tsx';
import supabaseClient from '@/api/supabaseClient.ts';
import usePastPapers from '@/api/usePastPapers.ts';
import useCurrentUser from '@/api/useCurrentUser.ts';
import useAllUserPaperAttempts from '@/api/useAllUserPaperAttempts';

const DownloadPastPapersPage: FC = () => {
  const navigate = useNavigate();

  // Fetch past papers from database
  const { data: pastPapers = [], isLoading } = usePastPapers();
  const { data: currentUser } = useCurrentUser();
  const { data: attempts = [] } = useAllUserPaperAttempts(currentUser?.auth_id);

  // Calculate average grades and attempt counts per paper
  const paperStats = useMemo(() => {
    const gradesByPaper: Record<string, number[]> = {};

    attempts.forEach((attempt) => {
      const paperId = attempt.past_paper_id;
      if (!gradesByPaper[paperId]) {
        gradesByPaper[paperId] = [];
      }
      gradesByPaper[paperId].push(attempt.gradeOutOf9);
    });

    // Calculate averages and counts
    const stats: Record<string, { avgGrade: number; attemptCount: number }> = {};
    Object.entries(gradesByPaper).forEach(([paperId, grades]) => {
      const sum = grades.reduce((acc, grade) => acc + grade, 0);
      stats[paperId] = {
        avgGrade: sum / grades.length,
        attemptCount: grades.length,
      };
    });

    return stats;
  }, [attempts]);

  // Group papers by year
  const papersByYear = useMemo(() => {
    const grouped: Record<string, typeof pastPapers> = {};

    pastPapers.forEach((paper) => {
      if (!paper.date) return;

      const year = new Date(paper.date).getFullYear().toString();

      if (!grouped[year]) {
        grouped[year] = [];
      }

      grouped[year].push(paper);
    });

    // Sort years in descending order
    const sortedYears = Object.keys(grouped).sort((a, b) => parseInt(b) - parseInt(a));

    return sortedYears.map((year) => ({
      year,
      papers: grouped[year].sort((a, b) => {
        // Sort by date within each year
        const dateA = a.date ? new Date(a.date).getTime() : 0;
        const dateB = b.date ? new Date(b.date).getTime() : 0;
        return dateA - dateB;
      }),
    }));
  }, [pastPapers]);

  const handleBack = () => {
    navigate(Routes.DASHBOARD);
  };

  const getPublicUrl = (fileName: string) => {
    const { data } = supabaseClient.storage.from('past-papers').getPublicUrl(fileName);
    return data.publicUrl;
  };

  const getMarkSchemeUrl = (pdfUrl: string) => {
    // Convert paper URL to mark scheme URL
    // e.g., "past-paper-xxx.pdf" -> "past-paper-xxx-mark-scheme.pdf"
    const markSchemeFileName = pdfUrl.replace('.pdf', '-mark-scheme.pdf');
    return getPublicUrl(markSchemeFileName);
  };

  const formatSession = (date: string | null) => {
    if (!date) return 'Unknown';
    const d = new Date(date);
    return d.toLocaleString('en-US', { month: 'long' });
  };

  return (
    <>
      <PageHeader title="Download Past Papers" showBackButton onBackClick={handleBack} titleAlign="left">
        <p className="text-sm text-gray-600 text-left -mt-4">Download exam papers and mark schemes for revision</p>
      </PageHeader>

      {/* Content */}
      <div className="flex-1 flex flex-col gap-6  p-2">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <span className="loading loading-spinner loading-lg text-primary"></span>
          </div>
        ) : papersByYear.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <p className="text-gray-500">No papers available</p>
          </div>
        ) : (
          <div className="space-y-8">
            {papersByYear.map(({ year, papers }) => (
              <div key={year} className="bg-white rounded-lg shadow-sm p-6">
                {/* Year Header */}
                <h2 className="text-xl font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">{year}</h2>

                {/* Table Header */}
                <div className="flex gap-6 mb-3 px-2">
                  <div className="text-sm font-medium text-gray-500 w-1/6">Month</div>
                  <div className="text-sm font-medium text-gray-500 w-3/6">Exam Paper</div>
                  <div className="text-sm font-medium text-gray-500 w-1/6">Mark Scheme</div>
                  <div className="text-sm font-medium text-gray-500 w-[80px]">Completed Attempts</div>
                  <div className="text-sm font-medium text-gray-500 w-[100px]">Avg. Grade</div>
                  <div className="flex-1"></div>
                </div>

                {/* Papers List */}
                <div className="space-y-2">
                  {papers.map((paper) => {
                    const stats = paperStats[paper.id];
                    return (
                      <div
                        key={paper.id}
                        className="flex gap-6 py-3 px-2 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        {/* Session */}
                        <div className="flex items-center w-1/6">
                          <span className="text-gray-700 font-medium">{formatSession(paper.date)}</span>
                        </div>

                        {/* Exam Paper */}
                        <div className="flex items-center w-3/6">
                          {paper.pdf_url ? (
                            <a
                              href={getPublicUrl(paper.pdf_url)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 text-primary hover:text-primary-focus transition-colors text-primary"
                            >
                              <span className="text-sm text-primary">{paper.title}</span>
                            </a>
                          ) : (
                            <span className="text-gray-400 text-sm">-</span>
                          )}
                        </div>

                        {/* Mark Scheme */}
                        <div className="flex items-center w-1/6">
                          {paper.pdf_url ? (
                            <a
                              href={getMarkSchemeUrl(paper.pdf_url)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 text-primary hover:text-primary-focus transition-colors"
                            >
                              <span className="text-sm text-primary">Mark Scheme</span>
                            </a>
                          ) : (
                            <span className="text-gray-400 text-sm">-</span>
                          )}
                        </div>

                        {/* Attempts */}
                        <div className="flex items-center w-[80px]">
                          {stats ? (
                            <span className="text-gray-700 text-sm">{stats.attemptCount}</span>
                          ) : (
                            <span className="text-gray-400 text-sm">-</span>
                          )}
                        </div>

                        {/* Average Grade */}
                        <div className="flex items-center w-[100px]">
                          {stats ? (
                            <span className="text-gray-700 font-semibold text-sm">
                              {Math.floor(stats.avgGrade)} {`[${stats.avgGrade.toFixed(1)}]`}
                            </span>
                          ) : (
                            <span className="text-gray-400 text-sm">-</span>
                          )}
                        </div>

                        {/* Empty space */}
                        <div className="flex-1"></div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default DownloadPastPapersPage;
